// BOARD & CARE PHOTOGRAPHS — the sequence is controlled by CareCraft's owner.
// PHOTO 1 = the primary/cover image in every context; PHOTO 2 and PHOTO 3 follow in the
// owner-specified order. Never derive the order from filenames, dimensions, or upload dates.
// Monogram Villa and Allure Senior Living photo imports are added as the owner's files
// become accessible; remaining gallery positions stay pending until then.
import monogramVillaPhoto1 from "../assets/facilities/monogram-villa-photo-1.jpg";
import monogramVillaPhoto2 from "../assets/facilities/monogram-villa-photo-2.jpg";
import monogramVillaPhoto3 from "../assets/facilities/monogram-villa-photo-3.jpg";
import allurePhoto1 from "../assets/facilities/allure-photo-1.jpg";
import allurePhoto2 from "../assets/facilities/allure-photo-2.jpg";
import allurePhoto3 from "../assets/facilities/allure-photo-3.jpg";
import estarPhoto1 from "../assets/facilities/estar-photo-1.jpg";
import estarPhoto2 from "../assets/facilities/estar-photo-2.jpg";
import estarPhoto3 from "../assets/facilities/estar-photo-3.jpg";
import jadeVillaPhoto1 from "../assets/facilities/jade-villa-photo-1.jpg";
import jadeVillaPhoto2 from "../assets/facilities/jade-villa-photo-2.jpg";
import jadeVillaPhoto3 from "../assets/facilities/jade-villa-photo-3.jpg";
import lovingCarePhoto1 from "../assets/facilities/loving-care-photo-1.jpg";
import lovingCarePhoto2 from "../assets/facilities/loving-care-photo-2.jpg";
import lovingCarePhoto3 from "../assets/facilities/loving-care-photo-3.jpg";

export const SITE = {
  name: "CareCraft",
  tagline: "Caring is our Craft.",
  proposition: "Finding the right care starts with understanding the person.",
  differentiator: "Curated, not just listed.",
  phoneDisplay: "(818) 590-1533",
  phoneHref: "tel:+18185901533",
  email: "doris@carecraft.us",
  url: "https://carecraft.us",
  area: "Los Angeles, California",
  founder: "Doris Laxamana-Rodriguez",
};

export const NAV_LINKS = [
  { label: "Find Care", to: "/find-care", testId: "nav-link-find-care" },
  { label: "How It Works", to: "/how-it-works", testId: "nav-link-how-it-works" },
  { label: "Care Options", to: "/care-options", testId: "nav-link-care-options" },
  { label: "For Professionals", to: "/for-professionals", testId: "nav-link-for-professionals" },
  { label: "About", to: "/about", testId: "nav-link-about" },
  { label: "Resources", to: "/resources", testId: "nav-link-resources" },
];

export const CATEGORY_LINE =
  "Board & Care • Independent Living • Assisted Living • Caregiver Support";

export const NAVIGATOR_PATHWAYS = [
  {
    id: "planning-ahead",
    label: "Planning for the future",
    body: "Explore care options with time to consider what feels right.",
  },
  {
    id: "support-at-home",
    label: "Finding more support at home",
    body: "Discover ways to make everyday life more supported and comfortable.",
  },
  {
    id: "new-place-to-call-home",
    label: "Exploring a new place to call home",
    body: "Consider residential options that match the person's needs and preferences.",
  },
  {
    id: "next-step-support",
    label: "Finding support for the next step",
    body: "Share what matters most and we'll help you explore the possibilities.",
  },
];

export const FIND_CARE_SECTIONS = [
  {
    id: "planning-ahead",
    title: "Planning for the future",
    body: "Exploring care options with time to consider what feels right gives a family room to breathe. Share what matters most, and CareCraft will help you understand the possibilities.",
    questions: [
      "What would make daily life feel easier and more connected?",
      "Which setting, community, or location feels like a good fit?",
      "What pace of change feels right for everyone?",
      "What matters most to the person and the family?",
    ],
    options:
      "Conversations may include Independent Living, Board & Care, Assisted Living, and caregiver support at home, explored with room to consider.",
  },
  {
    id: "support-at-home",
    title: "Finding more support at home",
    body: "Additional day-to-day support can make home feel comfortable and safe again. CareCraft helps families explore what that support could look like.",
    questions: [
      "Which parts of the day would feel easier with help?",
      "What kind of support would make the biggest difference?",
      "How does the person feel about welcoming someone into their home?",
      "What would sustainable look like for the family?",
    ],
    options:
      "Conversations may explore caregiver support at home as well as residential options like Board & Care and Assisted Living.",
  },
  {
    id: "new-place-to-call-home",
    title: "Exploring a new place to call home",
    body: "Some families are looking for a setting that fits the person's needs and preferences, with support close at hand. CareCraft helps narrow the possibilities to homes worth visiting.",
    questions: [
      "What would a good day look like in a new setting?",
      "What matters most: surroundings, community, proximity to family, budget?",
      "Which environment would help the person thrive?",
      "What pace feels right for a move?",
    ],
    options:
      "Conversations may include Board & Care, Independent Living, Assisted Living, and caregiver support, depending on the person's needs and circumstances.",
  },
  {
    id: "hospital-discharge",
    title: "Preparing for the next step after a hospital stay",
    body: "A hospital stay can be a moment to consider what comes next with clarity. CareCraft helps families understand what options may be appropriate and what can realistically be arranged.",
    questions: [
      "What has the care team shared about recovery and what comes next?",
      "Is home workable with the right support in place?",
      "What can be arranged before the discharge date?",
      "Who in the family can be part of the conversation?",
    ],
    options:
      "Conversations may range from caregiver support at home to residential options such as Board & Care or Assisted Living, depending on what recovery looks like.",
  },
  {
    id: "changing-needs",
    title: "Considering changing needs",
    body: "When daily routines or memory change, the right level of support can bring calm and connection back to every day. CareCraft helps families understand what may fit, now and down the road.",
    questions: [
      "Which moments matter most day to day?",
      "What structure and support would make days feel good?",
      "What would we want a care setting to understand about the person?",
      "What feels most important to the family right now?",
    ],
    options:
      "Conversations focus on the person: what is changing, what matters to them, and which settings may fit. No conclusion is needed before reaching out.",
  },
  {
    id: "fresh-eyes",
    title: "Looking at care with fresh eyes",
    body: "When the current care situation deserves a second look, CareCraft helps families reassess with clarity and consider alternatives, keeping everything that already works.",
    questions: [
      "What is working well, and what would we want to change?",
      "What would \"better\" look like for the person, not just on paper?",
      "What's a realistic timeline?",
      "What would we want to keep from the current arrangement?",
    ],
    options:
      "Conversations may revisit all four care options with fresh eyes on what has changed.",
  },
];

export const CARE_OPTIONS = [
  {
    id: "board-and-care",
    num: "01",
    title: "Board & Care",
    tagline: "A smaller, more residential environment.",
    body: "Community-based residences offering a home-like setting and personalized attention.",
    cta: "Learn about Board & Care",
  },
  {
    id: "independent-living",
    num: "02",
    title: "Independent Living",
    tagline: "More community while maintaining independence.",
    body: "For older adults who remain largely independent but value social connection, convenience, and access to support.",
    cta: "Explore Independent Living",
  },
  {
    id: "assisted-living",
    num: "03",
    title: "Assisted Living",
    tagline: "Support with everyday needs in a community setting.",
    body: "For older adults who value a residential community but need regular help with activities such as bathing, dressing, mobility, meals, or medication support within the community's licensed scope.",
    cta: "Understand Assisted Living",
  },
  {
    id: "caregiver-support",
    num: "04",
    title: "Caregiver Support",
    tagline: "When home is still the right place.",
    body: "CareCraft can help families explore caregiver support for everyday assistance at home.",
    cta: "Explore Caregiver Support",
  },
];

export const HOME_CARE_OPTIONS = [
  {
    id: "board-and-care",
    num: "01",
    title: "Board & Care",
    body: "A smaller residential setting with a home-like environment and day-to-day support.",
  },
  {
    id: "independent-living",
    num: "02",
    title: "Independent Living",
    body: "For older adults who remain largely independent and value community, convenience and social connection.",
  },
  {
    id: "assisted-living",
    num: "03",
    title: "Assisted Living",
    body: "For older adults who value independence while receiving regular help with everyday activities as needed.",
  },
  {
    id: "caregiver-support",
    num: "04",
    title: "Caregiver Support",
    body: "Additional day-to-day help for someone who prefers to continue living at home.",
  },
];

export const HOME_STEPS = [
  { num: "01", title: "Understand", body: "We begin with the person, their preferences and what matters most." },
  { num: "02", title: "Curate", body: "We bring together personally vetted options worth exploring." },
  { num: "03", title: "Explore", body: "Visit, ask questions and discover what feels right." },
  { num: "04", title: "Decide", body: "Choose the option that best fits the person and family." },
  { num: "05", title: "Transition", body: "Move forward with greater clarity and support." },
];

export const STEPS = [
  {
    num: "01",
    title: "Understand",
    lead: "We begin with the person.",
    body: "Tell us what is happening, what matters to your family, and what you're concerned about.",
    expect:
      "A conversation focused on the person: their situation, preferences, routines, concerns, and what matters to the family.",
    bring: "Whatever you have right now is enough. Working out the care category together is where we begin.",
  },
  {
    num: "02",
    title: "Curate",
    lead: "We narrow the possibilities.",
    body: "CareCraft identifies trusted options that may fit your family's particular situation rather than simply handing you a directory.",
    expect:
      "A short list of personally vetted options worth considering, with an honest explanation of why each one made the list.",
    bring: "A rough sense of what matters most to the person and the family. CareCraft will help you shape it.",
  },
  {
    num: "03",
    title: "Explore",
    lead: "We help you look more closely.",
    body: "We coordinate tours and help you understand what to look for, what to ask, and what differences matter.",
    expect:
      "Visits arranged around your timeline, with guidance on what to notice beyond the brochure: the environment, staff interactions, and daily rhythms.",
    bring: "The questions you want answered. We'll bring our own, too.",
  },
  {
    num: "04",
    title: "Decide",
    lead: "We help make the choices clearer.",
    body: "We talk through the options with you, answer questions, and help you compare what may fit. Your family remains at the center of the decision.",
    expect:
      "A clear comparison of the options and what each would mean day to day for the person you care about.",
    bring: "The people whose input matters to your family. Decisions land better when they're shared.",
  },
  {
    num: "05",
    title: "Transition",
    lead: "We stay alongside you through the next step.",
    body: "Once you've chosen, CareCraft helps make the transition into the new care arrangement as clear and manageable as possible.",
    expect: "Help with logistics, timing, and what to expect in the first weeks of a new arrangement.",
    bring: "Any worries. They're normal, and we've heard them before.",
  },
];

export const FAQS = [
  {
    q: "Does CareCraft's guidance cost anything?",
    a: "No. CareCraft's guidance is complimentary to families. You do not pay CareCraft for placement guidance.",
  },
  {
    q: "How is CareCraft compensated?",
    a: "When a placement is made, CareCraft is compensated by the care community. The cost of accommodation or care is paid directly to the provider and is separate from CareCraft's service.",
  },
  {
    q: "What areas do you serve?",
    a: "CareCraft is locally based in Los Angeles and currently serves Los Angeles.",
  },
  {
    q: "How quickly can you help?",
    a: "CareCraft responds to calls and emails within 24 hours.",
  },
  {
    q: "What types of senior care can you help us find?",
    a: "CareCraft's principal focus is Board & Care facilities, Independent Living facilities, Assisted Living, and Caregiver Support.",
  },
  {
    q: "How do you vet the places you recommend?",
    a: "CareCraft's service model is based on personally vetted recommendations rather than a directory listing.",
  },
  {
    q: "What if we don't know what level of care we need?",
    a: "That's a common starting point. Share what matters to you and what's changing, and CareCraft will help you understand which options may fit.",
  },
  {
    q: "Can you help after a hospital discharge?",
    a: "Yes. CareCraft can help families explore appropriate care options when a hospital discharge creates a need for additional support or a different living arrangement. The specific setting will depend on the person's needs and circumstances.",
  },
  {
    q: "Can you help if we need care urgently?",
    a: "CareCraft responds to calls and emails within 24 hours. We also understand that some situations leave families with only a short amount of time to plan. Contact us and tell us what is happening so we can understand how we may be able to help. CareCraft is not an emergency service. For a medical emergency, please contact the appropriate emergency services.",
  },
  {
    q: "Do you work with healthcare professionals?",
    a: "Yes. CareCraft works with healthcare professionals who are helping families plan for the next stage of care. Professional referrals can be made directly to Doris.",
  },
  {
    q: "What happens after we choose a community or care option?",
    a: "CareCraft stays involved through the next steps of the transition, helping the family understand what needs to happen and keeping communication clear as arrangements are made.",
  },
];

export const PRO_CHIPS = [
  "Social Workers",
  "Case Managers",
  "Discharge Planners",
  "Nurses",
  "Physicians",
  "Rehabilitation Teams",
  "Hospice Professionals",
];

export const GLOSSARY = [
  {
    term: "Board & Care",
    def: "A smaller residential setting, often in a home-like environment, for older adults who need day-to-day assistance and may benefit from a more intimate setting and closer familiarity with staff. In California, many Board & Care homes and Assisted Living communities operate under the Residential Care Facility for the Elderly licensing framework, although their size and setting can be quite different.",
  },
  {
    term: "Independent Living",
    def: "For older adults who are largely independent and want the convenience, community, and social opportunities of senior living without regular hands-on personal care as the central service.",
  },
  {
    term: "Assisted Living",
    def: "A residential community for older adults who value independence but need regular help with some everyday activities. Services vary by community and by the person's assessed needs.",
  },
  {
    term: "Caregiver Support",
    def: "Help provided in the person's own home with everyday activities and companionship, depending on the services arranged and the provider's permitted scope.",
  },
];

export const GLOSSARY_INTRO =
  "Understanding a few common care terms can make the choices a little easier to navigate. Part of CareCraft's role is helping you understand what the different options mean.";

export const TESTIMONIALS = {
  eyebrow: "Family experiences",
  heading: "What families say about working with CareCraft",
  intro:
    "Experiences shared by families and professionals CareCraft has had the privilege to support.",
  featured: {
    name: "Jennifer F.",
    location: "Woodland Hills, Los Angeles, CA",
    quote:
      "We did not know where to turn or how to get through all the steps and she was with us every single step throughout the entire process.",
    meta: "Yelp review · Jan 2, 2024",
  },
  page: {
    title: "Family Experiences",
    intro:
      "Experiences shared by families and professionals CareCraft has had the privilege to support.",
    reviews: [
      {
        name: "Jennifer F.",
        location: "Woodland Hills, Los Angeles, CA",
        meta: "Yelp review · Jan 2, 2024",
        text: [
          "Doris is truly a lifesaver! Last year our father's dementia worsened to the point that he was in need of additional care. My father being a very stubborn man was not fond of change. Doris helped me and my siblings move our father into the most amazing home and he was so cared for. We did not know where to turn or how to get through all the steps and she was with us every single step throughout the entire process. Our father lived his final days with the best care and Doris made a point of checking in on him during his stay. We are so grateful to her with all her support and assistance to find the best home for our dad. I highly recommend Doris and Care Craft!",
        ],
      },
      {
        name: "William S.",
        location: "Inglewood, CA",
        meta: "Yelp review · Jul 16, 2025",
        text: [
          "Doris was amazing at finding the perfect board and care facility for my elderly uncle. She took the stress out of what would otherwise be a very stressful situation for our family. She is extremely knowledgeable and really went out of her way to help us and guide us through this process. I highly recommend her services.",
        ],
      },
      {
        name: "Linda K.",
        location: "Simi Valley, CA",
        meta: "Yelp review · Apr 20, 2026",
        text: [
          "We were referred to Doris by our brothers care center. The process of finding your loved one the proper home is daunting, one that fits all their specific needs (space for walking in yard, gardening, accommodations for diet, a clean private room with a view of garden, kind caregivers, similar residents for stimulation and clean and warm home) we had a lot, and the first house she sent us to was perfect! We had already viewed 6 homes with another business, but she seemed to just understand our complete needs and we are so grateful.",
          "Doris is easy to communicate with, has an extreme expertise with her field in caregiving homes and very helpful with making the process easy and non stressful for the family.",
          "We appreciate everything and are so excited for our brother's next phase in healing and life.",
          "Thank you so much!",
        ],
      },
      {
        name: "Sarah W.",
        location: "Woodland Hills, Los Angeles, CA",
        meta: "Yelp review · May 8, 2026",
        text: [
          "Doris was beyond wonderful in helping me find the perfect place for my mom. It has been an incredibly difficult time for us and Doris helped us find the perfect place. Doris was supportive, compassionate and really helped address all my concerns. If you are going through what feels like a hopeless situation I recommend Doris because she was an angel.",
        ],
      },
      {
        name: "Sarah P.",
        location: "Washington, DC",
        meta: "Yelp review · May 28, 2026",
        text: [
          "Doris is amazing! She responded right away, gave us several options, and helped us through all parts of the process. we were so grateful to have worked with her.",
        ],
      },
      {
        name: "Don R.",
        location: "Bakersfield, CA",
        meta: "Yelp review · May 20, 2026",
        text: [
          "Doris is amazing. She's extremely professional kind and caring.",
          "She helped us through every step of the process and kept us updated. We appreciate everything that she has done.",
        ],
      },
    ],
  },
};

export const RESOURCES_INTRO =
  "Explore practical information designed to help families understand senior care options and move forward with greater clarity.";

export const HOW_CARECRAFT_HELPS = {
  heading: "How CareCraft can help",
  statement: "CareCraft helps families in Los Angeles understand and explore senior care options.",
  support:
    "A good guide will narrow the possibilities to a short, personally vetted list worth considering, while keeping the family's needs, preferences, and circumstances at the center of the process.",
};

export const COMPARE_NOTE =
  "Services and levels of support can vary by community. CareCraft can help you ask the right questions and understand what each option provides.";

// BOARD & CARE HOMES — listing, order, and photo sequences controlled by CareCraft's owner.
// RCFE license numbers and street addresses are intentionally not displayed on the site.
export const FACILITIES = [
  {
    id: "monogram-villa",
    num: 1,
    name: "Monogram Villa",
    location: "Sherman Oaks",
    // No room-type or availability claims supplied/approved for this home yet.
    photos: [
      // PHOTO 1 — PRIMARY / COVER (owner-specified)
      { slot: 1, src: monogramVillaPhoto1, alt: "Resident bedroom with adjustable care bed and decorative shelving at Monogram Villa in Sherman Oaks" },
      // PHOTO 2 (owner-specified)
      { slot: 2, src: monogramVillaPhoto2, alt: "Hallway with resident room doors at Monogram Villa in Sherman Oaks" },
      // PHOTO 3 (owner-specified)
      { slot: 3, src: monogramVillaPhoto3, alt: "Resident bedroom with upholstered bed and chandelier at Monogram Villa in Sherman Oaks" },
    ],
  },
  {
    id: "allure-senior-living",
    num: 2,
    name: "Allure Senior Living",
    location: "Sunland",
    // No room-type or availability claims supplied/approved for this home yet.
    photos: [
      // PHOTO 1 — PRIMARY / COVER (owner-specified)
      { slot: 1, src: allurePhoto1, alt: "Private resident bedroom with sitting area at Allure Senior Living in Sunland" },
      // PHOTO 2 (owner-specified)
      { slot: 2, src: allurePhoto2, alt: "Covered backyard patio at Allure Senior Living in Sunland" },
      // PHOTO 3 (owner-specified)
      { slot: 3, src: allurePhoto3, alt: "Hallway with grab bars at Allure Senior Living in Sunland" },
    ],
  },
  {
    id: "estar",
    num: 3,
    name: "Estar Board and Care",
    location: "Northridge",
    rooms: "Private and shared room options are available. Room availability may vary. Contact CareCraft to ask about current options.",
    photos: [
      // PHOTO 1 — PRIMARY / COVER (owner-specified)
      { slot: 1, src: estarPhoto1, alt: "Private resident bedroom with sitting chair at Estar Board and Care in Northridge" },
      // PHOTO 2 (owner-specified)
      { slot: 2, src: estarPhoto2, alt: "Private resident bedroom with dresser and television at Estar Board and Care in Northridge" },
      // PHOTO 3 (owner-specified)
      { slot: 3, src: estarPhoto3, alt: "Front exterior of Estar Board and Care in Northridge" },
    ],
  },
  {
    id: "jade-villa",
    num: 4,
    name: "Jade Villa Care Home",
    location: "Northridge",
    rooms: "Private and shared room options are available. Room availability may vary. Contact CareCraft to ask about current options.",
    photos: [
      // PHOTO 1 — PRIMARY / COVER (owner-specified)
      { slot: 1, src: jadeVillaPhoto1, alt: "Private resident bedroom at Jade Villa Care Home in Northridge" },
      // PHOTO 2 (owner-specified)
      { slot: 2, src: jadeVillaPhoto2, alt: "Common living room at Jade Villa Care Home in Northridge" },
      // PHOTO 3 (owner-specified)
      { slot: 3, src: jadeVillaPhoto3, alt: "Dining area and kitchen at Jade Villa Care Home in Northridge" },
    ],
  },
  {
    id: "loving-care-for-you",
    num: 5,
    name: "Loving Care for You",
    location: "Sherwood Forest",
    rooms: "Private and shared room options are available. Room availability may vary. Contact CareCraft to ask about current options.",
    photos: [
      // PHOTO 1 — PRIMARY / COVER (owner-specified)
      { slot: 1, src: lovingCarePhoto1, alt: "Common living room at Loving Care for You in Sherwood Forest" },
      // PHOTO 2 (owner-specified)
      { slot: 2, src: lovingCarePhoto2, alt: "Fireplace and television lounge at Loving Care for You in Sherwood Forest" },
      // PHOTO 3 (owner-specified)
      { slot: 3, src: lovingCarePhoto3, alt: "Dining area with fireplace at Loving Care for You in Sherwood Forest" },
    ],
  },
];

export const COMPENSATION_DISCLOSURE =
  "CareCraft's guidance is provided at no cost to families. When a placement is made, CareCraft is compensated by the care community. The cost of accommodation or care is paid directly to the provider and remains separate from CareCraft's service.";

export const EMERGENCY_NOTE =
  "CareCraft is not an emergency service. For medical emergencies, contact appropriate emergency services.";
